/**
 * Header
 *
 * App shell header with sidebar toggle, theme switch, alerts, and user menu.
 * Uses `useSidebar` to handle desktop vs mobile toggling.
 */
import React, { useState, useEffect } from 'react';
import { Moon, Sun } from 'lucide-react';
import { useSidebar } from '../../../context/sidebar';
import UserDropdown from './UserDropdown';
import AlertsDropdown from './AlertsDropdown';
import useDarkMode from '../../../hooks/useDarkMode';

function Header() {
  const isDark = useDarkMode();
  const { isMobileOpen, toggleSidebar, toggleMobileSidebar } = useSidebar();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Initial check
    handleResize();

    // Add event listener
    window.addEventListener('resize', handleResize);

    // Cleanup
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleToggle = () => {
    if (window.innerWidth >= 991) {
      toggleSidebar();
    } else {
      toggleMobileSidebar();
    }
  };

  const toggleTheme = () => {
    // Use the same pattern as LandingHeader - check the hook value directly
    if (isDark) {
      document.documentElement.classList.remove('dark');
      document.body.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      document.documentElement.classList.add('dark');
      document.body.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
  };

  return (
    <>
      <header className="flex w-full bg-white border-gray-200 z-50 dark:border-gray-800 dark:bg-gray-900 border-b">
        <div className="flex items-center justify-between w-full px-4 py-4">
          <div>
            <button
              className="flex items-center justify-center w-10 h-10 text-gray-500 hover:bg-gray-100 rounded-lg dark:text-gray-400 dark:hover:bg-gray-800"
              onClick={handleToggle}
              aria-label={isMobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileOpen}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-shrink-0"
              >
                <path
                  d="M4 6H20M4 12H20M4 18H20"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={toggleTheme}
              className="p-2 text-gray-500 rounded-lg hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
            >
              {isDark ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>

            {/* Alerts Dropdown now handles read/unread functionality */}
            <AlertsDropdown />

            <UserDropdown />
          </div>
        </div>
      </header>
    </>
  );
}

export default Header;